import { useState } from "react";
import { mfgInlineInputClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import type { MfgMaterial } from "@/lib/manufacturing/types";
import type {
  VariantConfig,
  VariantDeltaLine,
  VariantProportionalAddRule,
  VariantSlotScaleRule,
  VariantTransformRule,
} from "@/lib/manufacturing/variantConfig";
import { ManufacturingNamedVariantDefaultTransformRules } from "./ManufacturingNamedVariantDefaultTransformRules";
import { ManufacturingNamedVariantDeltaLinesEditor } from "./ManufacturingNamedVariantDeltaLinesEditor";
import { ManufacturingNamedVariantProportionalAddEditor } from "./ManufacturingNamedVariantProportionalAddEditor";
import { ManufacturingNamedVariantSlotScaleEditor } from "./ManufacturingNamedVariantSlotScaleEditor";

function rulesFromConfig(config: VariantConfig): VariantTransformRule[] {
  if ((config.mode === "transform" || config.mode === "hybrid") && config.rules.length > 0) {
    return config.rules;
  }
  return [];
}

function deltaLinesFromConfig(config: VariantConfig): VariantDeltaLine[] {
  if (config.mode === "delta" || config.mode === "hybrid") return config.delta_lines ?? [];
  return [];
}

function scaleRulesFromConfig(config: VariantConfig): VariantSlotScaleRule[] {
  if (config.mode === "delta" || config.mode === "hybrid") return config.slot_scale_rules ?? [];
  return [];
}

function proportionalRulesFromConfig(config: VariantConfig): VariantProportionalAddRule[] {
  if (config.mode === "delta" || config.mode === "hybrid") {
    return config.proportional_add_rules ?? [];
  }
  return [];
}

function summarizeConfig(config: VariantConfig): string {
  if (config.mode === "delta" || config.mode === "hybrid") {
    const scales = config.slot_scale_rules?.length ?? 0;
    const props = config.proportional_add_rules?.length ?? 0;
    const fixed = config.delta_lines?.length ?? 0;
    if (config.mode === "delta") {
      const parts: string[] = ["Delta"];
      if (scales > 0) parts.push(`${scales} scale`);
      if (props > 0) parts.push(`${props} proportional`);
      if (fixed > 0) parts.push(`${fixed} fixed`);
      return parts.length > 1 ? parts.join(" · ") : "Delta";
    }
    const n = config.rules.length;
    const parts = [`Hybrid · ${n} transform`];
    if (scales > 0) parts.push(`${scales} scale`);
    if (props > 0) parts.push(`${props} proportional`);
    if (fixed > 0) parts.push(`${fixed} fixed`);
    return parts.join(" · ");
  }
  const n = config.rules.length;
  return `Transform · ${n} rule${n === 1 ? "" : "s"}`;
}

function buildConfig(args: {
  mode: VariantConfig["mode"];
  rules: VariantTransformRule[];
  delta_lines: VariantDeltaLine[];
  slot_scale_rules: VariantSlotScaleRule[];
  proportional_add_rules: VariantProportionalAddRule[];
}): VariantConfig {
  const { mode, rules, delta_lines, slot_scale_rules, proportional_add_rules } = args;
  const deltaExtras = {
    ...(delta_lines.length > 0 ? { delta_lines } : {}),
    ...(slot_scale_rules.length > 0 ? { slot_scale_rules } : {}),
    ...(proportional_add_rules.length > 0 ? { proportional_add_rules } : {}),
  };
  if (mode === "transform") return { mode: "transform", rules };
  if (mode === "hybrid") {
    return { mode: "hybrid", rules, ...deltaExtras };
  }
  return { mode: "delta", ...deltaExtras };
}

export function ManufacturingNamedVariantDefaultTransformEditor({
  config,
  orgSlotLabels,
  prioritySlotLabels = [],
  materials = [],
  onChange,
  defaultCollapsed = false,
  namedVariantId,
  onError,
}: {
  config: VariantConfig;
  orgSlotLabels: readonly string[];
  /** Example-recipe / current-rule slots raised above the full org list. */
  prioritySlotLabels?: readonly string[];
  materials?: readonly MfgMaterial[];
  onChange: (next: VariantConfig) => void;
  /** When true, start collapsed (edit/view). Create flow usually leaves this false. */
  defaultCollapsed?: boolean;
  namedVariantId?: string | null;
  onError?: (message: string) => void;
}) {
  const [open, setOpen] = useState(!defaultCollapsed);
  const mode = config.mode;
  const rules = rulesFromConfig(config);
  const deltaLines = deltaLinesFromConfig(config);
  const scaleRules = scaleRulesFromConfig(config);
  const proportionalRules = proportionalRulesFromConfig(config);

  function setMode(next: VariantConfig["mode"]) {
    onChange(
      buildConfig({
        mode: next,
        rules: rulesFromConfig(config),
        delta_lines: deltaLinesFromConfig(config),
        slot_scale_rules: scaleRulesFromConfig(config),
        proportional_add_rules: proportionalRulesFromConfig(config),
      }),
    );
  }

  function setRules(nextRules: VariantTransformRule[]) {
    onChange(
      buildConfig({
        mode: mode === "delta" ? "hybrid" : mode,
        rules: nextRules,
        delta_lines: deltaLines,
        slot_scale_rules: scaleRules,
        proportional_add_rules: proportionalRules,
      }),
    );
  }

  function setDeltaLines(next: VariantDeltaLine[]) {
    onChange(
      buildConfig({
        mode: mode === "transform" ? "hybrid" : mode,
        rules,
        delta_lines: next,
        slot_scale_rules: scaleRules,
        proportional_add_rules: proportionalRules,
      }),
    );
  }

  function setScaleRules(next: VariantSlotScaleRule[]) {
    onChange(
      buildConfig({
        mode: mode === "transform" ? "hybrid" : mode,
        rules,
        delta_lines: deltaLines,
        slot_scale_rules: next,
        proportional_add_rules: proportionalRules,
      }),
    );
  }

  function setProportionalRules(next: VariantProportionalAddRule[]) {
    onChange(
      buildConfig({
        mode: mode === "transform" ? "hybrid" : mode,
        rules,
        delta_lines: deltaLines,
        slot_scale_rules: scaleRules,
        proportional_add_rules: next,
      }),
    );
  }

  const showTransform = mode === "transform" || mode === "hybrid";
  const showDelta = mode === "delta" || mode === "hybrid";

  return (
    <div className="rounded-lg border border-gray-200 dark:border-zinc-700">
      <button
        type="button"
        className="flex w-full items-center justify-between gap-2 px-2 py-2 text-left"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="text-xs font-semibold text-gray-700 dark:text-zinc-300">
          Default materials effect — variant template (all recipes)
        </span>
        <span className="text-[11px] text-gray-500 dark:text-zinc-400">
          {summarizeConfig(config)} · {open ? "Hide" : "Show"}
        </span>
      </button>
      {open ? (
        <div className="space-y-2 border-t border-gray-100 px-2 py-2 dark:border-zinc-800">
          <p className="text-[11px] text-gray-500 dark:text-zinc-400">
            Shared starting rules when this variant is applied to recipes.{" "}
            <strong>Hybrid</strong> runs a size-swap transform first, then applies deltas — same-slot
            qty changes merge onto the transformed material; other slots still append. Optional slot
            scale and proportional add supported — e.g. lap siding as base siding qty × factor.
          </p>
          <select
            className={`${mfgInlineInputClass} mb-2`}
            value={mode}
            onChange={(e) => setMode(e.target.value as VariantConfig["mode"])}
          >
            <option value="delta">Delta — add materials / scale slot qty</option>
            <option value="transform">
              Transform — rewrite parent slot materials (size swap)
            </option>
            <option value="hybrid">Hybrid — transform + delta</option>
          </select>

          {showTransform ? (
            <ManufacturingNamedVariantDefaultTransformRules
              rules={rules}
              materials={materials}
              orgSlotLabels={orgSlotLabels}
              prioritySlotLabels={prioritySlotLabels}
              onChange={setRules}
            />
          ) : null}

          {showDelta ? (
            <div className="space-y-4">
              <div>
                <div className="mb-1 text-[11px] font-semibold text-gray-600 dark:text-zinc-400">
                  Slot scale rules (estimate per recipe)
                </div>
                <ManufacturingNamedVariantSlotScaleEditor
                  rules={scaleRules}
                  orgSlotLabels={orgSlotLabels}
                  onChange={setScaleRules}
                />
              </div>
              <div>
                <div className="mb-1 text-[11px] font-semibold text-gray-600 dark:text-zinc-400">
                  Proportional add (different material × parent slot qty)
                </div>
                <ManufacturingNamedVariantProportionalAddEditor
                  rules={proportionalRules}
                  materials={materials}
                  orgSlotLabels={orgSlotLabels}
                  onChange={setProportionalRules}
                />
              </div>
              <div>
                <div className="mb-1 text-[11px] font-semibold text-gray-600 dark:text-zinc-400">
                  Fixed delta lines (same on every recipe)
                </div>
                <ManufacturingNamedVariantDeltaLinesEditor
                  lines={deltaLines}
                  materials={materials}
                  orgSlotLabels={orgSlotLabels}
                  onChange={setDeltaLines}
                  namedVariantId={namedVariantId}
                  onError={onError}
                />
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
